/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
Container,
Box,
Typography,
Button,
MenuItem,
Card,
CardContent,
CardActions,
Divider,
Chip,
CircularProgress,
Grid,
useMediaQuery,
useTheme,
Slide,
Fade,
Zoom,
InputAdornment,
TextField,
Paper,
FormControl,
InputLabel,
Select,
IconButton,
Tooltip,
Accordion,
AccordionSummary,
AccordionDetails
} from '@mui/material';
import {
Add as AddIcon,
ArrowForward as ArrowForwardIcon,
School as SchoolIcon,
Search,
Refresh,
Person,
CalendarToday,
Group,
AttachMoney,
TrendingUp,
Edit as EditIcon,
Delete as DeleteIcon,
ExpandMore,
TrackChanges,
Assessment,
Public,
Description,
Science
} from '@mui/icons-material';
import { APIProjectDelete, APIProjectList, APIResearcherList, APIUniversitiesList } from '../../utilities/APIS';
import { clearUserFilters, daysPassed, getIdCategory, getIdFullName, STORAGE_KEYS } from '../../utilities/Functions';
import NavigationMenu from '../navigation/NavigationMenu';
import { headers } from '../../utilities/Env';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import LoadingState from '../../components/LoadingState';

const Projects = ({ userOnly = false, userId = null }) => {
    const [projectsData, setProjectsData] = useState([]);
    const [researchersData, setResearchersData] = useState([]);
    const showToast = useCustomToast();
    const [universitiesData, setUniversitiesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingProductId, setDeletingProductId] = useState(null);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [sortOrder, setSortOrder] = useState('newest');
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedProject, setExpandedProject] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Initialize state from sessionStorage or props
    const [isUserOnlyMode, setIsUserOnlyMode] = useState(() => {
        const stored = sessionStorage.getItem(STORAGE_KEYS.USER_ONLY);
        return stored ? JSON.parse(stored) : (userOnly || (location.state?.userOnly || false));
    });

    const [targetUserId, setTargetUserId] = useState(() => {
        const stored = sessionStorage.getItem(STORAGE_KEYS.USER_ID);
        return stored ? JSON.parse(stored) : (userId || (location.state?.userId || null));
    });

    // Update sessionStorage when state changes
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.USER_ONLY, JSON.stringify(isUserOnlyMode));
    }, [isUserOnlyMode]);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.USER_ID, JSON.stringify(targetUserId));
    }, [targetUserId]);

    // Also handle location state updates
    useEffect(() => {
        const locationState = location.state || {};
        if (locationState.userOnly !== undefined) {
            setIsUserOnlyMode(locationState.userOnly);
        }
        if (locationState.userId !== undefined) {
            setTargetUserId(locationState.userId);
        }
    }, [location.state]);

    useEffect(() => {
        fetchData();
    }, [isUserOnlyMode, targetUserId]);

    useEffect(() => {
        // Component mount logic here
        return () => {
            // Cleanup function - runs when component unmounts
            const currentPath = location.pathname;
            if (!currentPath.startsWith('/project-')) {
                clearUserFilters();
            }
        };
    }, [location.pathname]);

    useEffect(() => {
        filterAndSortData();
    }, [projectsData, searchTerm, sortOrder, statusFilter, isUserOnlyMode, targetUserId]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [projectsRes, unisRes, researchersRes] = await Promise.all([
                axios.get(APIProjectList, { headers }),
                axios.get(APIUniversitiesList, { headers }),
                axios.get(APIResearcherList, { headers }),
            ]);

            const uni = Array.isArray(unisRes.data.data) ? unisRes.data.data.reverse() : [];
            setUniversitiesData(uni);

            const researchers = Array.isArray(researchersRes.data.data) ? researchersRes.data.data.reverse() : [];
            setResearchersData(researchers);

            let projects = Array.isArray(projectsRes.data.data) ? projectsRes.data.data.reverse() : [];

            // Filter for user's projects only if in user-only mode
            if (isUserOnlyMode && targetUserId) {
                projects = projects.filter(project => 
                    project.researcherName.toString() === targetUserId.toString()
                );
            }

            setProjectsData(projects);
            setFilteredData(projects);

        } catch (error) {
            console.log("error"+error);
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleShowAllProjects = () => {
        clearUserFilters();
        setIsUserOnlyMode(false);
        setTargetUserId(null);
    };

    const filterAndSortData = () => {
        let filtered = projectsData;

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(project =>
                project.projectTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getIdCategory(project.universityName, universitiesData)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getIdFullName(project.researcherName, researchersData)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.projectDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (project.coreInvestigator || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (project.objectives || '')?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by status
        if (statusFilter !== 'all') {
            filtered = filtered.filter(project => {
                const currentDate = new Date();
                const startDate = new Date(project.projectStart);
                const endDate = new Date(project.projectEnd);
                if (statusFilter === 'active') {
                    return currentDate >= startDate && currentDate <= endDate;
                } else if (statusFilter === 'upcoming') {
                    return currentDate < startDate;
                } else if (statusFilter === 'completed') {
                    return currentDate > endDate;
                }
                return true;
            });
        }

        // Sort data
        filtered = [...filtered].sort((a, b) => {
            switch (sortOrder) {
                case 'newest':
                    return new Date(b.uploadDate) - new Date(a.uploadDate);
                case 'oldest':
                    return new Date(a.uploadDate) - new Date(b.uploadDate);
                case 'title-asc':
                    return (a.projectTitle || '').localeCompare(b.projectTitle || '');
                case 'title-desc':
                    return (b.projectTitle || '').localeCompare(a.projectTitle || '');
                default:
                    return 0;
            }
        });

        setFilteredData(filtered);
    };

    const getHeaderText = () => {
        if (isUserOnlyMode) {
            return "My Research Projects";
        }
        return "Research Projects";
    };

    const getSubheaderText = () => {
        if (isUserOnlyMode) {
            return "Manage and view your research projects";
        }
        return "Explore innovative research projects from universities worldwide";
    };

    const getProjectStatus = (project) => {
        const currentDate = new Date();
        const startDate = new Date(project.projectStart);
        const endDate = new Date(project.projectEnd);

        if (currentDate < startDate) {
            return { status: 'Upcoming', color: 'info' };
        } else if (currentDate > endDate) {
            return { status: 'Completed', color: 'success' };
        } else {
            return { status: 'Active', color: 'warning' };
        }
    };

    const getProjectMembersCount = (project) => {
        try {
            const members = JSON.parse(project.projectMembers);
            return Array.isArray(members) ? members.length : 0;
        } catch {
            return 0;
        }
    };

    const getProjectMembers = (project) => {
        try {
            const members = JSON.parse(project.projectMembers);
            return Array.isArray(members) ? members : [];
        } catch {
            return [];
        }
    };

    const getProjectFundersCount = (project) => {
        try {
            const funders = JSON.parse(project.projectFunders);
            return Array.isArray(funders) ? funders.length : 0;
        } catch {
            return 0;
        }
    };

    const goToProjectDetails = (project) => {
        const detailsProps = {
            projectTitle: project.projectTitle,
            researcherName: project.researcherName,
            uploadDate: project.uploadDate,
            projectEnd: project.projectEnd,
            projectStart: project.projectStart,
            universityName: project.universityName,
            projectDescription: project.projectDescription,
            projectFunders: project.projectFunders,
            projectMembers: project.projectMembers,
            coreInvestigator: project.coreInvestigator,
            objectives: project.objectives,
            projectActivities: project.projectActivities,
            proposedBudget: project.proposedBudget,
            expectedOutcomes: project.expectedOutcomes,
            evaluationPlan: project.evaluationPlan,
            sustainabilityPlan: project.sustainabilityPlan
        };
        navigate("/project-details", { state: detailsProps });
    };

    const editProjectDetails = (project) => {
        const detailsProps = {
            projectId: project.id,
            projectTitle: project.projectTitle,
            researcherName: project.researcherName,
            uploadDate: project.uploadDate,
            projectEnd: project.projectEnd,
            projectStart: project.projectStart,
            universityName: project.universityName,
            projectDescription: project.projectDescription,
            projectFunders: project.projectFunders,
            projectMembers: project.projectMembers,
            coreInvestigator: project.coreInvestigator,
            objectives: project.objectives,
            projectActivities: project.projectActivities,
            proposedBudget: project.proposedBudget,
            expectedOutcomes: project.expectedOutcomes,
            evaluationPlan: project.evaluationPlan,
            sustainabilityPlan: project.sustainabilityPlan
        };
        navigate("/project-submit", { state: detailsProps });
    };

    const handleSubmitProject = () => {
        navigate("/project-submit");
    };

    const handleDelete = async (product) => {
        const confirmDelete = window.confirm(
            `🚨 Delete Confirmation\n\n` +
            `Project: ${product.projectTitle}\n` +
            `Researcher: ${getIdFullName(product.researcherName, researchersData)}\n` +
            `University: ${getIdCategory(product.universityName, universitiesData)}\n\n` +
            `This action cannot be undone. Are you sure you want to delete this product?`
        );

        if (!confirmDelete) {
            showToast("Cancelled", "Record deletion was cancelled", "info");
            return;
        }

        setDeletingProductId(product.id);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', product.id);

            const response = await axios.post(APIProjectDelete, formDataToSend, { headers });

            if (response.data.status === "success") {
                const successMessage = response.data.message || "Record deleted successfully!";
                showToast("Success", successMessage, "success");
                setProjectsData(prev => prev.filter(p => p.id !== product.id));
                setFilteredData(prev => prev.filter(p => p.id !== product.id));
                setTimeout(() => {
                    if (isUserOnlyMode && filteredData.length === 1) {
                        showToast("Info", "All your products have been deleted", "info");
                    }
                }, 1000);
            } else {
                throw new Error(response.data.message || "Unknown server error");
            }
        } catch (error) {
            let errorTitle = "Delete Failed";
            let errorMessage = "Failed to delete record. Please try again.";
            if (error.response) {
                const status = error.response.status;
                switch (status) {
                    case 422:
                        const errors = error.response.data.errors;
                        errorMessage = Object.values(errors).flat().join(', ') || "Validation error";
                        break;
                    case 404:
                        errorMessage = "Record not found. It may have been already deleted.";
                        break;
                    case 403:
                        errorMessage = "You don't have permission to delete this product.";
                        break;
                    case 401:
                        errorMessage = "Please log in to perform this action.";
                        break;
                    case 500:
                        errorMessage = "Server error. Please try again later.";
                        break;
                    default:
                        errorMessage = error.response.data?.message || `Server error (${status})`;
                }
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            } else if (error.code === "ECONNABORTED") {
                errorMessage = "Request timeout. Please check your connection and try again.";
            } else if (error.message) {
                errorMessage = error.message;
            }

            showToast(errorTitle, errorMessage, "error");
        } finally {
            setDeletingProductId(null);
        }
    };

    const handleExpandClick = (projectId) => {
        setExpandedProject(expandedProject === projectId ? null : projectId);
    };

    const hasAdditionalDetails = (project) => {
        return project.coreInvestigator || 
               project.objectives || 
               project.projectActivities || 
               project.proposedBudget || 
               project.expectedOutcomes || 
               project.evaluationPlan || 
               project.sustainabilityPlan;
    };

    const renderProjectDetailsAccordion = (project) => {
        const isExpanded = expandedProject === project.id;
        
        if (!hasAdditionalDetails(project)) {
            return null;
        }

        return (
            <Accordion 
                expanded={isExpanded} 
                onChange={() => handleExpandClick(project.id)}
                sx={{ 
                    mt: 2, 
                    boxShadow: 'none', 
                    border: `1px solid ${theme.palette.divider}`,
                    '&:before': { display: 'none' }
                }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Science fontSize="small" />
                        Project Details & Impact
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        {project.coreInvestigator && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Core Investigator
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    {project.coreInvestigator}
                                </Typography>
                            </Grid>
                        )}
                        
                        {project.objectives && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    <TrackChanges fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                    Objectives
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    {project.objectives}
                                </Typography>
                            </Grid>
                        )}
                        
                        {project.projectActivities && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    <Description fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                    Activities
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    {project.projectActivities}
                                </Typography>
                            </Grid>
                        )}
                        
                        {project.proposedBudget && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    <AttachMoney fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                    Proposed Budget
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    {project.proposedBudget}
                                </Typography>
                            </Grid>
                        )}
                        
                        {project.expectedOutcomes && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    <TrendingUp fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                    Expected Outcomes
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    {project.expectedOutcomes}
                                </Typography>
                            </Grid>
                        )}
                        
                        {project.evaluationPlan && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    <Assessment fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                    Evaluation Plan
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    {project.evaluationPlan}
                                </Typography>
                            </Grid>
                        )}
                        
                        {project.sustainabilityPlan && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    <Public fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                    Sustainability Plan
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    {project.sustainabilityPlan}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderTeamMembers = (project) => {
        const members = getProjectMembers(project);
        if (members.length === 0) return null;
        
        // Helper function to extract the actual name from member object
        const getMemberName = (member) => {
            if (typeof member === 'string') return member;
            if (typeof member === 'object') {
                return member.data || member.name || member.memberName || '';
            }
            return '';
        };
        
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Group color="primary" sx={{ fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                    <strong>Team Members:</strong>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {members.slice(0, 3).map((member, idx) => (
                        <Chip 
                            key={idx} 
                            label={getMemberName(member)} 
                            size="small" 
                            variant="outlined" 
                        />
                    ))}
                    {members.length > 3 && (
                        <Tooltip title={members.slice(3).map(m => getMemberName(m)).join(', ')}>
                            <Chip label={`+${members.length - 3} more`} size="small" />
                        </Tooltip>
                    )}
                </Box>
            </Box>
        );
    };

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <>
                <NavigationMenu />
                <LoadingState/>
            </>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <NavigationMenu />

            <Container Container maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
                {/* Header Section */}
                <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                    <Box sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2
                    }}>
                        <Box>
                            <Typography variant="h5" component="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {getHeaderText()}
                            </Typography>
                            <Typography sx={{ opacity: 0.9 }}>
                                {getSubheaderText()}
                            </Typography>
                            {isUserOnlyMode && (
                                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                                    Showing {projectsData.length} of your projects
                                </Typography>
                            )}
                        </Box>

                        <Button
                            variant="contained"
                            sx={{
                                bgcolor: 'white',
                                color: theme.palette.primary.main,
                                fontWeight: 'bold',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={handleSubmitProject}
                        >
                            Submit Project
                        </Button>
                    </Box>
                </Slide>

                {/* Search and Filter Section */}
                {(projectsData.length > 0 || searchTerm || statusFilter !== 'all') && (
                    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    placeholder="Search projects, researchers, universities, objectives..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search color="primary" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                    ✕
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Sort By</InputLabel>
                                    <Select
                                        value={sortOrder}
                                        label="Sort By"
                                        onChange={(e) => setSortOrder(e.target.value)}
                                    >
                                        <MenuItem value="newest">Newest First</MenuItem>
                                        <MenuItem value="oldest">Oldest First</MenuItem>
                                        <MenuItem value="title-asc">Title A-Z</MenuItem>
                                        <MenuItem value="title-desc">Title Z-A</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        label="Status"
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <MenuItem value="all">All Projects</MenuItem>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="upcoming">Upcoming</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <Button
                                    variant="outlined"
                                    onClick={fetchData}
                                    startIcon={<Refresh />}
                                    fullWidth
                                >
                                    Refresh
                                </Button>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                {isUserOnlyMode && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleShowAllProjects()}
                                        fullWidth
                                    >
                                        View All Projects
                                    </Button>
                                )}
                            </Grid>
                        </Grid>

                        {/* Results Count */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" color="text.secondary">
                                Showing {filteredData.length} of {projectsData.length} Projects
                                {isUserOnlyMode && " (Your Projects)"}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {searchTerm && (
                                    <Chip
                                        label={`Search: "${searchTerm}"`}
                                        onDelete={() => setSearchTerm('')}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {statusFilter !== 'all' && (
                                    <Chip
                                        label={`Status: ${statusFilter}`}
                                        onDelete={() => setStatusFilter('all')}
                                        color="secondary"
                                        variant="outlined"
                                    />
                                )}
                                {isUserOnlyMode && (
                                    <Chip
                                        label="My Projects Only"
                                        onDelete={() => navigate("/projects")}
                                        color="success"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* Projects List */}
                <Box>
                    {filteredData.length === 0 ? (
                        <Grid item xs={12}>
                            <Fade in={true} timeout={800}>
                                <Paper sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        No projects found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Try adjusting your search terms or filters
                                    </Typography>
                                </Paper>
                            </Fade>
                        </Grid>
                    ) : (
                        filteredData.map((project, index) => {
                            const status = getProjectStatus(project);
                            const fundersCount = getProjectFundersCount(project);
                            const isDeleting = deletingProductId === project.id;
                            const hasDetails = hasAdditionalDetails(project);

                            return (
                                <Grid item xs={12} key={project.id || index}>
                                    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                                        <Card elevation={2} sx={{
                                            borderLeft: `4px solid ${status.color === 'warning' ? theme.palette.warning.main : 
                                                          status.color === 'success' ? theme.palette.success.main : 
                                                          theme.palette.info.main}`,
                                            transition: 'transform 0.3s, box-shadow 0.3s',
                                            mb: 2,
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: theme.shadows[8]
                                            }
                                        }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    flexDirection: isMobile ? 'column' : 'row',
                                                    gap: 2,
                                                    mb: 2
                                                }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                            {project.projectTitle}
                                                        </Typography>
                                                        <Typography variant="body1" color="text.secondary" paragraph>
                                                            {project.projectDescription?.substring(0, 200)}
                                                            {project.projectDescription?.length > 200 && '...'}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: isMobile ? 'flex-start' : 'flex-end',
                                                        gap: 1
                                                    }}>
                                                        <Chip
                                                            label={status.status}
                                                            color={status.color}
                                                            size="small"
                                                            variant="filled"
                                                        />
                                                        <Chip
                                                            icon={<SchoolIcon />}
                                                            label={getIdCategory(project.universityName, universitiesData)}
                                                            color="primary"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        {project.coreInvestigator && (
                                                            <Chip
                                                                icon={<Person />}
                                                                label={`PI: ${project.coreInvestigator}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Divider sx={{ my: 2 }} />

                                                {/* Project Metadata */}
                                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Person color="primary" sx={{ fontSize: 20 }} />
                                                            <Typography variant="body2">
                                                                <strong>Researcher:</strong> {getIdFullName(project.researcherName, researchersData)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CalendarToday color="primary" sx={{ fontSize: 20 }} />
                                                            <Typography variant="body2">
                                                                <strong>Period:</strong> {project.projectStart} - {project.projectEnd}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6} md={3}>
                                                        {renderTeamMembers(project)}
                                                    </Grid>

                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AttachMoney color="primary" sx={{ fontSize: 20 }} />
                                                            <Typography variant="body2">
                                                                <strong>Funders:</strong> {fundersCount} organizations
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>

                                                {/* Additional Info */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 1
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Uploaded {daysPassed(project.uploadDate)} days ago
                                                    </Typography>
                                                    {hasDetails && (
                                                        <Chip
                                                            icon={<Science />}
                                                            label="Has Detailed Plan"
                                                            size="small"
                                                            color="info"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>

                                                {/* Expandable Details Section */}
                                                {renderProjectDetailsAccordion(project)}
                                            </CardContent>

                                            <CardActions sx={{
                                                justifyContent: 'space-between',
                                                p: 2,
                                                pt: 0,
                                                borderTop: `1px solid ${theme.palette.divider}`
                                            }}>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        icon={<TrendingUp />}
                                                        label="Research Project"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Box>

                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {/* Edit Button - Only show for user's own products in user-only mode */}
                                                    {isUserOnlyMode && (
                                                        <>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                endIcon={<EditIcon />}
                                                                onClick={() => editProjectDetails(project)}
                                                                size="small"
                                                                disabled={isDeleting}
                                                                sx={{
                                                                    backgroundColor: theme.palette.success.main,
                                                                    color: 'white',
                                                                    '&:hover': {
                                                                        backgroundColor: theme.palette.secondary.main,
                                                                    }
                                                                }}
                                                            >
                                                                Edit
                                                            </Button>
                                    
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                endIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                                                                onClick={() => handleDelete(project)}
                                                                size="small"
                                                                disabled={isDeleting}
                                                            >
                                                                {isDeleting ? 'Deleting...' : 'Delete'}
                                                            </Button>
                                                        </>
                                                    )}

                                                    <Button
                                                        variant="contained"
                                                        endIcon={<ArrowForwardIcon />}
                                                        onClick={() => goToProjectDetails(project)}
                                                        size="small"
                                                        disabled={isDeleting}
                                                    >
                                                        View Details
                                                    </Button>
                                                </Box>
                                            </CardActions>
                                        </Card>
                                    </Zoom>
                                </Grid>
                            );
                        })
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default Projects;